/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React, {useState} from 'react';
import {
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import withSignals from '@ion/applications/src/components/hoc/withSignals';
import {translate} from '@ion/core/src/locale/locale';
import IonSpinner from '@ion/dls/src/components/loading/IonSpinner';
import DashboardShare from '@ion/dls/src/icons/DashboardShare';
import FolderShared from '@ion/dls/src/icons/FolderShared';
import FolderSharedOpened from '@ion/dls/src/icons/FolderSharedOpened';
import Render from '@ion/dls/src/render/Render';
import {toggleThemeStyle} from '@ion/dls/src/theme/helpers';
import {dlsColor} from '@ion/dls/src/theme/theme';
import useThemeContext from '@ion/dls/src/theme/useThemeContext';
import ScheduledReportMenu from './ScheduledReportMenu';
import {
  openScheduledReportsFromDashboardRow,
  setCreateScheduledReport,
  setCreatingScheduledReport,
  setDuplicatingScheduledReport,
  setEditingScheduledReport,
  setSelectedDashboardReport,
  setShowAddScheduledReportEditor
} from './store';
import {translateFolderOrDashboard} from './utils';

const INDENTATION = 24;

/**
 * Represents a single dashboard entry.
 */
export interface Dashboard {
  oid: string;
  title: string;
  [key: string]: any;
}

/**
 * Represents a dashboard category containing multiple dashboards.
 */
export interface DashboardCategory {
  name: string;
  dashboards: Dashboard[];
  source?: { oid?: string };
  color?: string;
  folders?: DashboardCategory[];
}

/**
 * Props for the DashboardList component.
 */
export interface DashboardListProps {
  categories: DashboardCategory[];
  openCategory: Record<string, boolean>;
  selectedCategory?: string;
  toggleCategory: (categoryName: string | string[], value?: boolean) => void;
  selected: Dashboard | null;
  setSelected: (dashboard: Dashboard | null) => void;
  isLoading?: boolean;
  showScheduledReports?: boolean;
};

const useStyles = makeStyles((theme) => ({
  nested : {
    paddingTop    : 0,
    paddingBottom : 0
  },
  listRoot : {
    paddingTop    : 0,
    paddingBottom : 0,
    padding       : toggleThemeStyle('0', '8px 16px 8px 16px'),
    height        : toggleThemeStyle('32px', '44px'),
    '&:hover'     : {
      backgroundColor : `${dlsColor('lightBlue')} !important`
    }
  },
  selectedItem : {
    backgroundColor : `${dlsColor('lightBlue')} !important`,
    '&:hover'       : {
      backgroundColor : `${dlsColor('skyBlue')} !important`
    }
  },
  icon : {
    minWidth : '32px'
  },
  listText : toggleThemeStyle({
    fontSize     : '14px',
    color        : dlsColor('grey2'),
    whiteSpace   : 'nowrap',
    overflow     : 'hidden',
    textOverflow : 'ellipsis'
  }, {
    color         : dlsColor('grey2'),
    fontSize      : '14px',
    overflow      : 'hidden',
    fontFamily    : 'Inter',
    fontWeight    : 400,
    lineHeight    : '20px',
    whiteSpace    : 'nowrap',
    textOverflow  : 'ellipsis',
    letterSpacing : '0'
  }),
  loader : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center',
    width          : '100%',
    height         : '100%'
  },
  noCategories : toggleThemeStyle({
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center',
    width          : '100%',
    height         : 'calc(100vh - 240px)'
  }, {
    color          : dlsColor('grey1'),
    width          : '100%',
    height         : 'calc(100vh - 240px)',
    display        : 'flex',
    fontSize       : '16px',
    alignItems     : 'center',
    fontFamily     : 'Inter',
    fontWeight     : 500,
    lineHeight     : '14px',
    letterSpacing  : '0',
    justifyContent : 'center'
  }),
  menuOpenItem : {
    backgroundColor : `${dlsColor('lightBlue')} !important`
  }
}));

/**
 * DashboardList renders a vertical list of expandable dashboard categories.
 * Each category can be toggled open/closed to show its dashboards.
 * Highlights the currently selected dashboard.
 *
 * While loading, a centered spinner is shown instead.
 *
 * ```typescript
 * <DashboardList
 *   categories={dashboardCategories}
 *   openCategory={{ Sales: true, Finance: false }}
 *   toggleCategory={(name) => setOpenCategory((prev) => ({ ...prev, [name]: !prev[name] }))}
 *   selected={selectedDashboard}
 *   setSelected={(dash) => setSelectedDashboard(dash)}
 *   isLoading={false}
 * />
 * ```
 */
const DashboardList = ({
  categories,
  openCategory,
  toggleCategory,
  selected,
  setSelected,
  selectedCategory,
  isLoading = false,
  showScheduledReports = false
}: DashboardListProps) => {
  const classes = useStyles();

  const [
    openMenuDashboardOid,
    setOpenMenuDashboardOid
  ] = useState<string | null>(null);

  const {
    isLegacy : isLegacyTheme
  } = useThemeContext();

  const onDashboardClick = function(dash: Dashboard) {
    openScheduledReportsFromDashboardRow();
    setSelectedDashboardReport?.({
      oid   : dash?.oid,
      title : dash?.title
    });
    setEditingScheduledReport?.(false);
    setCreatingScheduledReport?.(true);
    setCreateScheduledReport?.(null);
    setDuplicatingScheduledReport?.(false);
    setShowAddScheduledReportEditor?.(true);
  };

  if (isLoading && !categories?.length) {
    return (
      <div className={classes.loader}>
        <IonSpinner />
      </div>
    );
  }

  const renderCategories = (categories: DashboardCategory[], level = 0) => {
    const paddingLeft = {
      folder    : (level + 1) * INDENTATION,
      dashboard : (level + 2) * INDENTATION
    };
    return categories.map(category => {
      const {
        source,
        name,
        color,
        dashboards = [],
        folders = []
      } = category;
      const isCategorySelected = !selected && (name === selectedCategory);
      const className          = level === 0 ? undefined : classes.nested;
      const isOpen             = openCategory[name];

      const onToggleCategory = function() {
        const isOpen = openCategory[name];
        if (isOpen && folders?.length) {
          const foldersToClose: string[] = [];
          const collectFolderNames = (children: DashboardCategory[]) => {
            children.forEach(child => {
              foldersToClose.push(child.name);
              if (child.folders?.length) {
                collectFolderNames(child.folders);
              }
            });
          };
          collectFolderNames(folders);
          foldersToClose.push(name);
          toggleCategory(foldersToClose, false);
          return;
        }
        toggleCategory(name);
      };

      const FolderComponent = isOpen ? FolderSharedOpened : FolderShared;

      return (
        <React.Fragment key={`${name}-${level}`}>
          <ListItem button id={name}
            selected={isCategorySelected}
            classes={{root: classes.listRoot, selected: classes.selectedItem}}
            onClick={onToggleCategory}
            className={className}
            style={{
              ...(isLegacyTheme && {
                paddingLeft : paddingLeft.folder
              })
            }}>
            <ListItemIcon classes={{root: classes.icon}}>
              <FolderComponent folderColor={color} />
            </ListItemIcon>
            <ListItemText classes={{primary: classes.listText}}
              primary={translateFolderOrDashboard(source?.oid, name, 'folder')}/>
          </ListItem>

          <Collapse in={isOpen} timeout='auto' unmountOnExit>
            <List component='div' disablePadding>
              {folders?.length > 0 && renderCategories(folders, level + 1)}
              {dashboards.map(dash => {
                const {
                  title,
                  oid
                } = dash;
                const onSetSelected = function() {
                  setCreateScheduledReport?.(null);
                  setDuplicatingScheduledReport?.(false);
                  setSelected(dash);
                  toggleCategory(name, true);
                };
                const isMenuOpen = openMenuDashboardOid === oid;
                return (
                  <ListItem button id={title}
                    key={oid}
                    className={`${className || ''} ${isMenuOpen ? classes.menuOpenItem : ''}`}
                    style={{paddingLeft: paddingLeft.dashboard}}
                    selected={selected?.oid === oid}
                    onClick={onSetSelected}
                    classes={{root: classes.listRoot, selected: classes.selectedItem}}>
                    <ListItemIcon classes={{root: classes.icon}}>
                      <DashboardShare folderColor={category?.color} />
                    </ListItemIcon>
                    <ListItemText classes={{primary: classes.listText}}
                      primary={translateFolderOrDashboard(oid, title, 'dashboard')}/>
                    <Render.If condition={showScheduledReports}>
                      <ScheduledReportMenu dashboard={dash}
                        onClose={function() {
                          setOpenMenuDashboardOid(null);
                        }}
                        onOpen={setOpenMenuDashboardOid}
                        onScheduleReport={onDashboardClick} />
                    </Render.If>
                  </ListItem>
                );
              })}
            </List>
          </Collapse>
        </React.Fragment>
      );
    });
  };

  return (
    <>
      <List>
        <Render>
          <Render.If condition={categories?.length}>
            {renderCategories(categories)}
          </Render.If>
          <Render.Else>
            <div className={classes.noCategories}>
              {translate('sisense:analytics.noCategories')}
            </div>
          </Render.Else>
        </Render>
      </List>
    </>
  );
};

export default withSignals(DashboardList);
